import { getPath, PathUnit } from '@/app/consts/path.const';
import { RoleEnum } from '@/app/consts/role.const';
import { environment } from '@/environments/environment';

export interface AppLink {
  label: string;
  icon?: string;
  route?: string;
  children?: AppLink[];
  roles: RoleEnum[];
  isQuickLink?: boolean;
  isHideInSideNav?: boolean;
  quickLinkRoute?: string;
  roleAuthGuard?: boolean; // default true
}

export const AllAppLinks: AppLink[] = [{ 
  label: 'Ask Bot', 
  icon: 'message', 
  route: getPath(PathUnit.AskBot), 
  roles: [RoleEnum.Basic, RoleEnum.Extended, RoleEnum.Complete, RoleEnum.SuperUser], 
  isQuickLink: true,
}, { 
  label: 'Node Validator', 
  icon: 'key', 
  route: getPath(PathUnit.NodeValidator), 
  roles: [RoleEnum.Basic, RoleEnum.Extended, RoleEnum.Complete, RoleEnum.SuperUser], 
  isQuickLink: true 
}, { 
  label: 'Prerequisites', 
  icon: 'tick', 
  route: getPath(PathUnit.PreRequisite), 
  roles: [RoleEnum.Basic, RoleEnum.Extended, RoleEnum.Complete, RoleEnum.SuperUser], 
  isQuickLink: true 
}, { 
  label: 'Job Lookup', 
  icon: 'search', 
  // 侧边栏名称保持 "Job Lookup"，路由为 job-lookup，加载 JobStatusComponent 保持原有组件逻辑
  route: getPath(PathUnit.JobLookup), 
  roles: [RoleEnum.Basic, RoleEnum.Extended, RoleEnum.Complete, RoleEnum.SuperUser], 
  isQuickLink: true 
}, { 
  label: 'Application Owner', 
  icon: 'profile', 
  isQuickLink: true,
  quickLinkRoute: getPath(PathUnit.SoftwareCenter), 
  roles: [RoleEnum.Extended, RoleEnum.Complete, RoleEnum.SuperUser],
  children: [{ 
    label: 'Software Centre', 
    route: getPath(PathUnit.SoftwareCenter), 
    roles: [RoleEnum.Extended, RoleEnum.Complete, RoleEnum.SuperUser], 
  }], 
}, {
  label: 'Support Agent', 
  icon: 'support', 
  isQuickLink: true,
  quickLinkRoute: getPath(PathUnit.JobLogAnalysis), // use first child as quick link
  roles: [RoleEnum.Complete, RoleEnum.SuperUser] ,
  children: [{
    label: 'Job Log Analysis',
    route: getPath(PathUnit.JobLogAnalysis),
    roles: [RoleEnum.Basic, RoleEnum.Extended, RoleEnum.Complete, RoleEnum.SuperUser],
  }, {
    label: 'Job Log Query',
    route: getPath(PathUnit.LogQueryTop),
    roles: [RoleEnum.Complete, RoleEnum.SuperUser],
  }, {
    label: 'Live Monitoring',
    route: getPath(PathUnit.LiveMonitoring),
    roles: [RoleEnum.Complete, RoleEnum.SuperUser],
  }], 
}, {
  label: 'Administrator', 
  icon: 'tool', 
  roles: [RoleEnum.SuperUser], 
  isQuickLink: true,
  quickLinkRoute: getPath(PathUnit.NotificationsMgmt), // use first child as quick link
  children: [{ 
    label: 'Notifications', 
    route: getPath(PathUnit.NotificationsMgmt), 
    roles: [RoleEnum.SuperUser], 
  }, { 
    label: 'Manage Application Owner', 
    route: getPath(PathUnit.SystemOwnersMgmt), 
    roles: [RoleEnum.SuperUser], 
  }, {
    label: 'AI Content Manager', 
    route: getPath(PathUnit.AIContentManager),
    roles: [RoleEnum.SuperUser], 
  }, {
    label: 'AI Quality Dashboard', 
    route: getPath(PathUnit.QualityDashboard),
    roles: [RoleEnum.SuperUser], 
    isHideInSideNav: environment.region === 'cn', // hide in cn region;
  }, {
    label: 'Clear Cache', 
    route: getPath(PathUnit.ClearCache),
    roles: [RoleEnum.SuperUser], 
    isQuickLink: false,
    isHideInSideNav: true,
  }],
}, {
  label: 'Request Access', 
  icon: 'link', 
  route: getPath(PathUnit.RequestAccess), 
  roles: [RoleEnum.Guest, RoleEnum.Basic, RoleEnum.Extended, RoleEnum.Complete, RoleEnum.SuperUser], 
  isQuickLink: true, 
  isHideInSideNav: true 
}, {
  label: 'Software Center Upload', 
  icon: 'upload',
  route: getPath(PathUnit.SoftwareCenterUpload),
  roles: [RoleEnum.Complete, RoleEnum.SuperUser],
  isQuickLink: false,
  isHideInSideNav: true,
}, {
  label: 'System Settings', 
  icon: 'setting',
  route: getPath(PathUnit.SystemSettings),
  roles: [RoleEnum.SuperUser],
  isQuickLink: false,
  isHideInSideNav: true,
}];

export function getNavItemsByRole(role: RoleEnum): AppLink[] {
  function filterLinks(links: AppLink[]): AppLink[] {
    return links.reduce<AppLink[]>((acc, link) => {
      const children = link.children ? filterLinks(link.children) : undefined;
      if (link.roles.includes(role) || (children && children.length > 0)) {
        acc.push({ ...link, children });
      }
      return acc;
    }, []);
  }
  return filterLinks(AllAppLinks);
}


export function getQuickLinksByRole(role: RoleEnum): AppLink[] {
  const result: AppLink[] = [];
  function collect(links: AppLink[]) {
    for (const link of links) {
      if (link.isQuickLink && link.roles.includes(role)) {
        result.push(link);
      }
      if (link.children) {
        collect(link.children);
      }
    }
  }
  collect(AllAppLinks);
  return result;
}

export function getRolesByLink(labelOrRoute: string): RoleEnum[] {
  let roles: RoleEnum[] = [];
  function findLink(links: AppLink[]): boolean {
    for (const link of links) {
      if (link.label === labelOrRoute || link.route === labelOrRoute) {
        roles = link.roles;
        return true;
      }
      if (link.children && findLink(link.children)) {
        return true;
      }
    }
    return false;
  }
  findLink(AllAppLinks);
  return roles;
}
